import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import AccordionCard from "../../../../../components/common/PL/AccordionCard/AccordionCard";
import AccessTypeMethodCard from "../../../../../components/common/PL/Cards/AccessTypeMethodCard/AccessTypeMethodCard";
import DirectionsWalkIcon from "@material-ui/icons/DirectionsWalk";
import DriveEtaIcon from "@material-ui/icons/DriveEta";
import MotorcycleIcon from "@material-ui/icons/Motorcycle";
import DirectionsBikeIcon from "@material-ui/icons/DirectionsBike";
import BundleKey from "../../../../../translation/bundleKey";
import BicycleContentForm from "./BicycleContentForm/BicycleContentForm";
import VehicleContentForm from "./VehicleContentForm/VehicleContentForm";
import "./AccessVehicleForm.css";
import { RegistrationMode } from "../../registroIngresoNavigation";

const ACCESS_VEHICLE_METHODS = [
  {
    type: "walk",
    labelKey: BundleKey.ACCESS_VEHICLE_METHOD_WALK,
    icon: <DirectionsWalkIcon fontSize="inherit" />,
  },
  {
    type: "vehicle",
    labelKey: BundleKey.ACCESS_VEHICLE_METHOD_VEHICLE,
    icon: (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <DriveEtaIcon fontSize="inherit" />
        <MotorcycleIcon fontSize="inherit" />
      </div>
    ),
  },
  {
    type: "bike",
    labelKey: BundleKey.ACCESS_VEHICLE_METHOD_BIKE,
    icon: <DirectionsBikeIcon fontSize="inherit" />,
  },
];

type AccessVehicleFormProps = {
  formId: string;
  mode: RegistrationMode;
  vehicles?: any[];
};

const AccessVehicleForm = ({ formId, mode, vehicles }: AccessVehicleFormProps) => {
  const {
    setValue,
    watch,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useFormContext();
  const vehicleType = watch("vehicleType");

  const selectedVehicleMethod = useMemo(() => {
    switch (vehicleType) {
      case "none":
        return "walk";
      case "bike":
        return "bike";
      case "car":
        return "vehicle";
      default:
        return undefined; // Represent no selection instead of defaulting to walk
    }
  }, [vehicleType]);

  const titleKey =
    mode === "exit"
      ? BundleKey.ACCESS_VEHICLE_FORM_TITLE_EXIT
      : BundleKey.ACCESS_VEHICLE_FORM_TITLE;

  // Register vehicleType as required
  React.useEffect(() => {
    register("vehicleType", { required: "El medio de acceso es obligatorio" });
    return () => {
      unregister("vehicleType");
    };
  }, [register, unregister]);

  // Keep validation in sync
  React.useEffect(() => {
    setValue("vehicleType", vehicleType || null, {
      shouldValidate: isSubmitted,
    });
  }, [vehicleType, setValue, isSubmitted]);

  const hasVehicleTypeError = isSubmitted && !!errors.vehicleType;

  const renderSelectedAccessContent = () => {
    switch (selectedVehicleMethod) {
      case "bike":
        return <BicycleContentForm />;
      case "vehicle":
        return <VehicleContentForm vehicles={vehicles} mode={mode} />;
      default:
        return null;
    }
  };

  return (
    <AccordionCard id={`${formId}-access-vehicle`} titleKey={titleKey} number={3} expanded>
      <div className="accessVehicleFormContainer">
        <div className="accessVehicleMethodGrid" data-field-name="vehicleType">
          {ACCESS_VEHICLE_METHODS.map((option) => (
            <AccessTypeMethodCard
              key={option.type}
              labelKey={option.labelKey}
              icon={option.icon}
              selected={selectedVehicleMethod === option.type}
              hasError={hasVehicleTypeError}
              onClick={() => {
                const mappedValue =
                  option.type === "walk" ? "none" : option.type === "vehicle" ? "car" : "bike";
                setValue("vehicleType", mappedValue, { shouldValidate: isSubmitted });
              }}
            />
          ))}
        </div>
        {hasVehicleTypeError && (
          <span className="accessTypeMethodError">Debe seleccionar un medio de acceso</span>
        )}

        {renderSelectedAccessContent()}
      </div>
    </AccordionCard>
  );
};

export default AccessVehicleForm;
